import { useEffect, useState } from 'react';
import { errorMessage } from '../lib/api';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { Button, ButtonLink, ButtonRow } from '../ui';

/**
 * The four states a screen can be in instead of showing its content, plus the note a list
 * shows when it is legitimately empty.
 *
 * Kept in one file, and deliberately one shape: a panel, a heading, a sentence that says
 * what happened, and — where there is one — the way out, as a full-height control. They are
 * read one at a time and weeks apart, so any difference between them reads to the client as
 * a different kind of problem.
 */
export function LoadingPanel({ label }: { label: string }) {
  // Mounted empty, filled a beat later: a live region present at mount, with its content set
  // afterwards, is what makes assistive tech notice the DOM mutation. One that arrives already
  // holding its own text — which this did, since `role="status"` and `label` mounted together
  // on the same node — is exactly the case screen readers routinely skip. Nested inside the
  // visible `<p>` rather than as its own flex child: `.vh` is `position: absolute`, and design
  // guardrails §3.4 wants it inside an in-flow wrapper when its parent is a flex container.
  const [announced, setAnnounced] = useState('');
  useEffect(() => {
    setAnnounced(label);
  }, [label]);

  return (
    <div className="panel state-panel state-loading">
      <span className="state-indicator" aria-hidden="true" />
      <p className="sub" style={{ margin: 0 }}>
        {label}
        <span className="vh" role="status" aria-live="polite">
          {announced}
        </span>
      </p>
    </div>
  );
}

interface ErrorPanelProps {
  error: unknown;
  onRetry?: () => void;
  /**
   * True where this panel replaces the whole screen (a shell gate, a route with no other
   * heading) rather than sitting inside a page that already has its own `<h1>` — the board
   * and history pages show this alongside a `PageHeading`, and a second `<h1>` there would be
   * a heading-hierarchy violation of its own. Defaults to the inline case, since that is most
   * call sites; a whole-page caller opts in.
   */
  pageHeading?: boolean;
}

export function ErrorPanel({ error, onRetry, pageHeading = false }: ErrorPanelProps) {
  const Heading = pageHeading ? 'h1' : 'h2';
  return (
    <div className="panel state-panel" role="alert">
      <Heading className={pageHeading ? 'h1' : 'sec'}>Something went wrong</Heading>
      <p className="sub" style={{ marginTop: 0 }}>
        {errorMessage(error)}
      </p>
      {/* Says nothing about whether the attempt took effect, because from here that is not
          knowable — a failed request and a request that succeeded after the connection
          dropped look identical. Naming a person to ask is the only next step that is
          always true. */}
      <p className="sub" style={{ marginTop: 0 }}>
        If this keeps happening, your account manager can look into it.
      </p>
      {onRetry ? (
        <div style={{ marginTop: 12 }}>
          <ButtonRow>
            <Button onClick={onRetry}>Try again</Button>
          </ButtonRow>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The 404 surface. The API answers 404 for "missing" and for "belongs to another
 * organisation" alike, so this copy must not imply the item exists somewhere.
 */
export function NotFoundPanel({ what = 'page' }: { what?: string }) {
  useDocumentTitle('Not found');
  return (
    <div className="panel state-panel">
      <h1 className="h1">Not found</h1>
      <p className="sub" style={{ marginTop: 0 }}>
        We could not find that {what}. It may have been removed, or the link may be wrong.
      </p>
      <div style={{ marginTop: 12 }}>
        <ButtonRow>
          <ButtonLink to="/board">Back to the board</ButtonLink>
        </ButtonRow>
      </div>
    </div>
  );
}

/**
 * No project behind this organization.
 *
 * Staff see this on a screen that needs one — the board, history, intake — while the shell
 * around it stays put, because the way out is in that shell: the organization switcher, and
 * the Admin screen that connects a repository. An early return above the shell is what turns
 * this into a dead end.
 */
export function NoProjectPanel({ staff }: { staff: boolean }) {
  return (
    <div className="panel state-panel">
      <h1 className="h1">No project connected</h1>
      <p className="sub" style={{ marginTop: 0 }}>
        {staff
          ? 'This organisation has no project yet. Connect a repository from Admin, or switch to another organisation.'
          : 'No project has been connected to your account. Your account manager can set one up.'}
      </p>
      {staff ? (
        <div style={{ marginTop: 12 }}>
          <ButtonRow>
            {/* Named exactly as the tab that leads there. It read "Go to administration"
                while the tab said "Admin", which is two names for one screen. */}
            <ButtonLink to="/admin">Go to Admin</ButtonLink>
          </ButtonRow>
        </div>
      ) : null}
    </div>
  );
}

/**
 * A valid session that belongs to no organisation.
 *
 * Two ways in: an invitation that has been signed in against but never accepted, and a
 * membership that was removed while the browser still held the cookie. Both are recoverable
 * by the person only through a link somebody else sends them, so this says who to ask rather
 * than offering a control that cannot help.
 *
 * Signing out is offered because the other real case is the wrong ACCOUNT — someone who
 * accepted on a personal address and is now signed in on a work one sees exactly this.
 */
export function NoOrganizationPanel() {
  return (
    <div className="panel state-panel">
      <h1 className="h1">Your account is not in an organisation yet</h1>
      <p className="sub" style={{ marginTop: 0 }}>
        You are signed in, but this account has not been given access to a project. If you were sent
        an invitation, open the link in that email and press Accept invitation — that is what grants
        the access.
      </p>
      <p className="sub" style={{ marginTop: 0 }}>
        If you have already accepted, or the link no longer works, your account manager can send a
        new invitation.
      </p>
      <div style={{ marginTop: 12 }}>
        <ButtonRow>
          <ButtonLink to="/sign-in">Sign in as someone else</ButtonLink>
        </ButtonRow>
      </div>
    </div>
  );
}

export function EmptyNote({ children }: { children: React.ReactNode }) {
  return <div className="empty">{children}</div>;
}
